import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { FlowsheetConstants } from "../FlowsheetConstants";
import { CareElementFrequencyType } from "../Models/CareElementFrequencyType";
import { CareComponent } from "../Models/CareElements/CareComponent";
import { CareElementType } from "../Models/CareElements/CareElementType";
import { ImmunizationCareElement } from "../Models/CareElements/ImmunizationCareElement";
import { ICareComponentService } from "./ICareComponentService";

class ImmunizationsCareComponentService implements ICareComponentService {
    public processCareComponent(careComponent: CareComponent, patientContactId: string, encounterDate: Date): void {
        if (!encounterDate) {
            console.error("Encounter date is not defined in ImmunizationsCareComponentService");
            return;
        }

        const influenzaCareElement = careComponent
            ?.CareElements
            .firstOrDefault(p => p.Name == FlowsheetConstants.Immunizations.InfluenzaCareElementName);

        const pneumococcalCareElement = careComponent
            ?.CareElements
            .firstOrDefault(p => p.Name == FlowsheetConstants.Immunizations.PneumococcalCareElementName);

        if (!influenzaCareElement && !pneumococcalCareElement)
            return;

        const allImmunizations = ImmunizationsCareComponentService.getAllImmunizations(patientContactId, encounterDate);

        if (influenzaCareElement && influenzaCareElement.CareElementType === CareElementType.Immunization) {
            ImmunizationsCareComponentService.processImmunizationCareElement(
                influenzaCareElement,
                allImmunizations,
                ImmunizationsCareComponentService.InfluenzaAgents,
                encounterDate
            );
        }

        if (pneumococcalCareElement && pneumococcalCareElement.CareElementType === CareElementType.Immunization) {
            ImmunizationsCareComponentService.processImmunizationCareElement(
                pneumococcalCareElement,
                allImmunizations,
                ImmunizationsCareComponentService.PneumococcalAgents,
                encounterDate
            );
        }
    }

    public static getAllImmunizations(patientContactId: string, encounterDate: Date): MedicalItemImmunization[] {
        const medicalSummary = patientStore.PatientMedicalSummaryModels[patientContactId];
        if (!medicalSummary)
            return [];

        const previousEncounterIds = patientStore.PatientMedicalSummaryModels[patientContactId]
            ?.get("Encounters")
            .filter(p => new Date(p.ConsultationDate).toDateOnly() <= new Date(encounterDate).toDateOnly())
            .map(p => p.Id)
            .toArray();

        const immunizations = medicalSummary
            .get("MedicalItemsImmunization")
            .filter(immunization =>
                new Date(this.getImmunizationDate(immunization)).toDateOnly() <= new Date(encounterDate).toDateOnly() &&
                previousEncounterIds.includes(immunization.RelatedEncounterId) &&
                immunization.EMRTemplateIsInvalid !== true
            )
            .toArray();

        return immunizations;
    }

    private static processImmunizationCareElement(
        careElement: ImmunizationCareElement,
        allImmunizations: MedicalItemImmunization[],
        agentList: string[],
        encounterDate: Date
    ): void {
        const filteredImmunizations = allImmunizations.filter(immunization =>
            agentList.some(agent => agent.toLowerCase() === immunization.Type?.trim()?.toLowerCase())
        );

        const lastImmunization = filteredImmunizations
            ?.sortByPropertyDesc(immunization => ImmunizationsCareComponentService.getImmunizationDate(immunization))
            ?.firstOrDefault();

        if (lastImmunization) {
            careElement.IsRefused = lastImmunization.IsRefused ?? false;
            careElement.Date = lastImmunization.IsRefused
                ? lastImmunization.RefusedDate
                : lastImmunization.AdministeredDate;
        }
        else {
            careElement.IsRefused = null;
            careElement.Date = null;
        }

        careElement.IsOverdue = ImmunizationsCareComponentService.isImmunizationOverdue(careElement, filteredImmunizations, encounterDate);
    }

    private static getImmunizationDate(immunization: MedicalItemImmunization): Date {
        return immunization.RefusedDate ?? immunization.AdministeredDate ?? immunization.CreationDate;
    }

    private static isImmunizationOverdue(
        careElement: ImmunizationCareElement,
        allImmunizations: MedicalItemImmunization[],
        encounterDate: Date
    ): boolean {
        if (careElement.Frequency === CareElementFrequencyType.None || !encounterDate)
            return false;

        const lastNonRefusedImmunization = allImmunizations
            ?.filter(immunization => immunization.IsRefused !== true)
            ?.sortByPropertyDesc(immunization => immunization.AdministeredDate ?? immunization.CreationDate)
            ?.firstOrDefault();

        if (!lastNonRefusedImmunization)
            return true;

        const luxonEncounterDate = LuxonDateTimeParser.parse(encounterDate);
        const luxonLastImmunizationDate = LuxonDateTimeParser.parse(lastNonRefusedImmunization?.AdministeredDate ?? lastNonRefusedImmunization?.CreationDate);

        switch (careElement.Frequency) {
            case CareElementFrequencyType.Annual:
                return luxonLastImmunizationDate.plus({ years: 1 }) < luxonEncounterDate;
            case CareElementFrequencyType.ThreeMonths:
                return luxonLastImmunizationDate.plus({ months: 3 }) < luxonEncounterDate;
            case CareElementFrequencyType.SixMonths:
                return luxonLastImmunizationDate.plus({ months: 6 }) < luxonEncounterDate;
            case CareElementFrequencyType.Biennial:
                return luxonLastImmunizationDate.plus({ years: 2 }) < luxonEncounterDate;
            default:
                return false;
        }
    }

    public static readonly InfluenzaAgents: string[] = [
        "Inf"
    ];

    public static readonly PneumococcalAgents: string[] = [
        "Pneu",
        "Pneu-C",
        "Pneu C 10",
        "Pneu C 13",
        "Pneu C 15",
        "Pneu P 23"
    ];
}

export { ImmunizationsCareComponentService };
